import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Download, X, ArrowRight } from "lucide-react";
import { getInstallState, promptInstall, subscribeInstallState, subscribeUpdateAvailable, applyUpdate } from "@/lib/pwa";
import { toast } from "sonner";

/**
 * Two-in-one PWA affordance:
 *  1. Compact <InstallButton /> for the Navbar — shows only when the browser
 *     has fired `beforeinstallprompt` (Chrome/Edge/Android) OR when running
 *     on iOS Safari (falls back to the Share-sheet how-to modal). Hidden
 *     when already running standalone.
 *  2. <InstallBanner /> — a floating bottom bar first-time visitors see
 *     until they either install or dismiss. Auto-hides once installed.
 *  3. <UpdateToast /> — surfaces when a new SW is waiting.
 */

const DISMISS_KEY = "jixxeo_pwa_dismissed_at";
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function useInstallState() {
  const [state, setState] = useState(() => getInstallState());
  useEffect(() => subscribeInstallState(setState), []);
  return state;
}

export function InstallButton({ compact = false }) {
  const { canInstall, isStandalone, isIos } = useInstallState();
  const [iosOpen, setIosOpen] = useState(false);
  if (isStandalone) return null;
  if (!canInstall && !isIos) return null;

  const handle = async () => {
    if (isIos && !canInstall) {
      setIosOpen(true);
      return;
    }
    const res = await promptInstall();
    if (res.outcome === "accepted") {
      toast.success("Jixxeo is installing to your home screen ⚡");
    }
  };

  return (
    <>
      <button
        data-testid="pwa-install-btn"
        onClick={handle}
        className={`hidden md:inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-full border border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors ${compact ? "" : ""}`}
        title="Install Jixxeo to your home screen"
      >
        <Download className="w-3.5 h-3.5" /> Install App
      </button>
      <IosInstructionsModal open={iosOpen} onClose={() => setIosOpen(false)} />
    </>
  );
}

export function InstallBanner() {
  const { canInstall, isStandalone, isIos } = useInstallState();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);
  const onHome = pathname === "/";

  useEffect(() => {
    let installed = false;
    try { installed = localStorage.getItem("jixxeo_pwa_installed") === "1"; } catch {}
    if (!onHome || isStandalone || installed) { setVisible(false); return; }
    try {
      const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (t && Date.now() - t < DISMISS_MS) { setVisible(false); return; }
    } catch {}
    const id = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(id);
  }, [canInstall, isStandalone, isIos, onHome]);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  };

  const install = async () => {
    if (!canInstall) { setIosOpen(true); return; }
    const res = await promptInstall();
    if (res.outcome === "accepted") {
      try { localStorage.setItem("jixxeo_pwa_installed", "1"); } catch {}
      toast.success("Installing JixxeoApps to your device");
      setVisible(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            data-testid="pwa-install-banner"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 260 }}
            className="fixed bottom-3 inset-x-3 sm:inset-x-auto sm:right-6 sm:bottom-6 z-[70] sm:max-w-md"
          >
            <div className="relative rounded-2xl p-4 pr-11 bg-[#050505]/95 backdrop-blur-xl border border-[#00F0FF]/35 shadow-[0_10px_40px_rgba(0,240,255,0.25)]">
              <button
                onClick={dismiss}
                data-testid="pwa-install-dismiss"
                className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                aria-label="Dismiss install banner"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3.5">
                <img src="/icon-192.png" alt="JixxeoApps logo" data-testid="pwa-install-logo"
                  className="w-14 h-14 rounded-2xl ring-1 ring-[#00F0FF]/50 shadow-[0_0_24px_rgba(0,240,255,0.45)] shrink-0" />
                <div className="min-w-0">
                  <div className="font-heading font-black text-base sm:text-lg leading-tight" data-testid="pwa-install-title">
                    Install JixxeoApps for Quick Access
                  </div>
                  <div className="text-[12px] text-white/60 mt-0.5">
                    {canInstall ? "Opens full-screen from your home screen or desktop." : "Add it to your home screen or desktop in a few taps."}
                  </div>
                  <button
                    onClick={install}
                    data-testid="pwa-install-cta"
                    className="mt-3 inline-flex items-center gap-1.5 bg-[#00F0FF] hover:bg-white text-black font-black mono text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-full transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> Install Now
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <IosInstructionsModal open={iosOpen} onClose={() => setIosOpen(false)} />
    </>
  );
}

function installSteps() {
  const ua = navigator.userAgent || "";
  const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document);
  const inFrame = (() => { try { return window.self !== window.top; } catch { return true; } })();
  if (inFrame) return { title: "Open JixxeoApps in its own tab", steps: ["Installing isn't possible inside an embedded preview window.", "Open jixxeoapps.com directly in Chrome, Edge, Samsung Internet or Safari.", "Then tap Install Now on the banner."] };
  if (ios && !/Safari/.test(ua) || /CriOS|FxiOS|EdgiOS/.test(ua)) return { title: "Install on iPhone / iPad", steps: ["Tap the Share button in your browser (iOS 16.4+), or open this page in Safari.", "Choose \"Add to Home Screen\".", "Tap Add — JixxeoApps appears on your Home Screen."] };
  if (ios) return { title: "Install on iPhone / iPad", steps: ["Tap the Share button at the bottom of Safari.", "Scroll and choose \"Add to Home Screen\".", "Tap Add — JixxeoApps appears on your Home Screen with its own icon."] };
  if (/Android/.test(ua)) return { title: "Install on Android", steps: ["Open your browser menu (⋮ at the top or bottom).", "Tap \"Install app\" or \"Add to Home screen\".", "Confirm — JixxeoApps appears in your app drawer."] };
  if (/Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua)) return { title: "Install on Mac (Safari 17+)", steps: ["In the menu bar choose File → Add to Dock.", "Click Add — JixxeoApps opens in its own window."] };
  if (/Firefox/.test(ua)) return { title: "Install on desktop", steps: ["Firefox desktop doesn't support installing web apps.", "Open jixxeoapps.com in Chrome or Microsoft Edge.", "Click the install icon in the address bar, or Install Now on this banner."] };
  return { title: "Install on desktop", steps: ["Click the install icon at the right of the address bar (Chrome / Edge).", "Or open the browser menu → \"Install JixxeoApps\".", "If you previously uninstalled it, reload the page and try again."] };
}

function IosInstructionsModal({ open, onClose }) {
  const info = open ? installSteps() : null;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={onClose}
          data-testid="pwa-ios-modal"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-strong rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 border border-[#00F0FF]/25 relative"
          >
            <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3">
              <img src="/icon-192.png" alt="" className="w-10 h-10 rounded-xl" />
              <h3 className="font-heading font-black text-xl uppercase tracking-tighter" data-testid="pwa-install-help-title">{info.title}</h3>
            </div>
            <ol className="mt-5 space-y-3">
              {info.steps.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center mono text-xs font-bold shrink-0">{i + 1}</span>
                  <div className="text-sm text-white/80">{t}</div>
                </li>
              ))}
            </ol>
            <button onClick={onClose}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-black mono text-xs uppercase tracking-widest py-3 rounded-full transition-colors">
              Got it <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function UpdateToast() {
  useEffect(() => subscribeUpdateAvailable(() => {
    toast("A new version of Jixxeo is ready", {
      description: "Refresh to pick up the latest apps and features.",
      duration: Infinity,
      action: {
        label: "Refresh",
        onClick: () => applyUpdate(),
      },
    });
  }), []);
  return null;
}
